import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { BarChart3, Search, Calendar, Briefcase, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { Business, UploadMetadata } from '../types';
import toast from 'react-hot-toast';

interface CompareData {
  metadata: UploadMetadata;
  businesses: Business[];
  rawText?: string;
  fileType?: string;
}

const Compare = () => {
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const [selectedUpload, setSelectedUpload] = useState<CompareData | null>(null);
  const [compareUpload, setCompareUpload] = useState<CompareData | null>(null);
  const [availableUploads, setAvailableUploads] = useState<UploadMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const WORD_REGEX = useMemo(() => /[\p{L}\p{N}'-]+/gu, []);
  const TOKEN_REGEX = useMemo(() => /[\p{L}\p{N}'-]+|[^\p{L}\p{N}'-]+/gu, []);

  const getWords = (text: string) =>
    (text.match(WORD_REGEX) || []).map(w => w.toLowerCase());

  const buildHighlightedHtml = (text: string, uniqueWords: Set<string>) => {
    const tokens = text.match(TOKEN_REGEX) || [];
    return tokens
      .map(token => {
        const lower = token.toLowerCase();
        if (uniqueWords.has(lower)) {
          return `<span class="bg-red-100 text-red-700 px-1 rounded">${token}</span>`;
        }
        return token;
      })
      .join('');
  };

  const normalizeText = (upload: CompareData | null) => {
    if (!upload) return '';
    const raw = (upload as any).rawText ?? (upload.metadata as any)?.rawText;
    if (raw && typeof raw === 'string' && raw.trim()) return raw;
    if (upload.businesses?.length) return JSON.stringify(upload.businesses, null, 2);
    return 'Aucun contenu disponible pour ce fichier.';
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;

      try {
        const selectedId = searchParams.get('id');
        if (selectedId) {
          const docRef = doc(db, 'uploads', selectedId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setSelectedUpload({
              metadata: {
                id: docSnap.id,
                ...data
              } as UploadMetadata,
              businesses: data.businesses,
              rawText: data.rawText,
              fileType: data.fileType
            });

            const uploadsQuery = query(
              collection(db, 'uploads'),
              where('userId', '==', currentUser.uid)
            );

            const querySnapshot = await getDocs(uploadsQuery);
            const uploadsData = querySnapshot.docs
              .map(doc => ({
                id: doc.id,
                ...doc.data()
              } as UploadMetadata))
              .filter(upload => upload.id !== selectedId);

            setAvailableUploads(uploadsData);
          } else {
            toast.error('Selected dataset not found');
          }
        } else {
          const uploadsQuery = query(
            collection(db, 'uploads'),
            where('userId', '==', currentUser.uid)
          );

          const querySnapshot = await getDocs(uploadsQuery);
          const uploadsData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as UploadMetadata));

          setAvailableUploads(uploadsData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, searchParams]);

  const handleInitialSelect = async (uploadId: string) => {
    try {
      const docRef = doc(db, 'uploads', uploadId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSelectedUpload({
          metadata: {
            id: docSnap.id,
            ...data
          } as UploadMetadata,
          businesses: data.businesses,
          rawText: data.rawText,
          fileType: data.fileType
        });

        const uploadsQuery = query(
          collection(db, 'uploads'),
          where('userId', '==', currentUser?.uid)
        );

        const querySnapshot = await getDocs(uploadsQuery);
        const uploadsData = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          } as UploadMetadata))
          .filter(upload => upload.id !== uploadId);

        setAvailableUploads(uploadsData);
        setCompareUpload(null);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load dataset');
    }
  };

  const handleCompareSelect = async (uploadId: string) => {
    try {
      const docRef = doc(db, 'uploads', uploadId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (selectedUpload?.metadata.fileType && data.fileType && selectedUpload.metadata.fileType !== data.fileType) {
          toast.error('Impossible de comparer des fichiers de formats différents');
          return;
        }
        setCompareUpload({
          metadata: {
            id: docSnap.id,
            ...data
          } as UploadMetadata,
          businesses: data.businesses,
          rawText: data.rawText,
          fileType: data.fileType
        });
      }
    } catch (error) {
      console.error('Error fetching comparison data:', error);
      toast.error('Failed to load comparison data');
    }
  };

  const getComparisonResult = () => {
    if (!selectedUpload || !compareUpload) return null;

    const typeA = selectedUpload.metadata.fileType;
    const typeB = compareUpload.metadata.fileType;
    if (typeA && typeB && typeA !== typeB) {
      return { mismatch: true };
    }

    const leftText = normalizeText(selectedUpload);
    const rightText = normalizeText(compareUpload);

    const leftWords = getWords(leftText);
    const rightWords = getWords(rightText);

    const leftSet = new Set(leftWords);
    const rightSet = new Set(rightWords);

    const leftUnique = new Set(Array.from(leftSet).filter(word => !rightSet.has(word)));
    const rightUnique = new Set(Array.from(rightSet).filter(word => !leftSet.has(word)));

    return {
      mismatch: false,
      leftText,
      rightText,
      leftCount: leftWords.length,
      rightCount: rightWords.length,
      leftUnique,
      rightUnique
    };
  };

  const filteredUploads = availableUploads.filter(upload => {
    const searchLower = searchTerm.toLowerCase();
    const locationMatch = (upload.location || '').toLowerCase().includes(searchLower);
    const dateSource = upload.dateCollecte || upload.dateUpload;
    const dateMatch = dateSource ? format(new Date(dateSource), 'PP').toLowerCase().includes(searchLower) : false;
    const metierMatch = (upload.metier || upload.fileName || '').toLowerCase().includes(searchLower);
    const typeMatch = !selectedUpload?.metadata.fileType
      || !upload.fileType
      || upload.fileType === selectedUpload.metadata.fileType;

    return typeMatch && (locationMatch || dateMatch || metierMatch);
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const compareResult = getComparisonResult();

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white p-8 rounded-lg shadow-md">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-800">Comparer deux fichiers</h1>
          </div>
        </div>

        {!selectedUpload ? (
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-800 mb-4">
              Sélectionner un premier fichier
            </h2>
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Rechercher par nom ou date..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              {searchTerm && filteredUploads.length === 0 && (
                <p className="text-sm text-gray-500 mt-2">
                  Aucun fichier trouvé
                </p>
              )}
            </div>

            <div className="grid gap-4">
              {filteredUploads.map((upload) => (
                <div
                  key={upload.id}
                  className="border rounded-lg p-4 cursor-pointer hover:border-blue-200 transition-colors"
                  onClick={() => handleInitialSelect(upload.id)}
                >
                  <div className="flex justify-between items-center">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">{upload.fileName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          {format(new Date(upload.dateCollecte || upload.dateUpload), 'PP')}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-sm text-gray-500 block">{upload.recordCount} lignes</span>
                      <span className="text-xs text-gray-400 uppercase">{upload.fileType || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-800 mb-4">Fichier sélectionné</h2>
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-sm text-gray-600">Nom</span>
                    <p className="font-medium">{selectedUpload.metadata.fileName}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Date</span>
                    <p className="font-medium">
                      {format(new Date(selectedUpload.metadata.dateCollecte || selectedUpload.metadata.dateUpload), 'PP')}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Format</span>
                    <p className="font-medium uppercase">{selectedUpload.metadata.fileType || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-800 mb-4">
                Choisir le fichier à comparer
              </h2>
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Rechercher par nom ou date..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                {searchTerm && filteredUploads.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    Aucun fichier trouvé
                  </p>
                )}
              </div>

              <div className="grid gap-4">
                {filteredUploads.map((upload) => (
                  <div
                    key={upload.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors
                      ${compareUpload?.metadata.id === upload.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'hover:border-blue-200'}`}
                    onClick={() => handleCompareSelect(upload.id)}
                  >
                    <div className="flex justify-between items-center">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-gray-500" />
                          <span className="font-medium">{upload.fileName}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            {format(new Date(upload.dateCollecte || upload.dateUpload), 'PP')}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-sm text-gray-500 block">{upload.recordCount} lignes</span>
                        <span className="text-xs text-gray-400 uppercase">{upload.fileType || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {compareResult && (
          <div className="mt-8 space-y-4">
            {compareResult.mismatch ? (
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-lg">
                <AlertTriangle className="text-red-600 h-5 w-5" />
                <div>
                  <p className="font-medium text-red-800">Les fichiers doivent avoir le même format pour être comparés.</p>
                  <p className="text-sm text-red-700">Sélectionnez deux fichiers .txt ensemble, ou deux .xlsx, etc.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-medium text-gray-800">Résultat de comparaison</h2>
                    <p className="text-sm text-gray-500">Les mots différents sont surlignés en rouge.</p>
                  </div>
                  <div className="flex gap-4 text-sm text-gray-600">
                    <span>{compareResult.leftCount} mots (fichier 1)</span>
                    <span>|</span>
                    <span>{compareResult.rightCount} mots (fichier 2)</span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-gray-800">Fichier 1</p>
                      <span className="text-xs text-gray-500 uppercase">{selectedUpload?.metadata.fileType || 'N/A'}</span>
                    </div>
                    <div
                      className="text-sm text-gray-800 whitespace-pre-wrap leading-6"
                      dangerouslySetInnerHTML={{ __html: buildHighlightedHtml(compareResult.leftText, compareResult.leftUnique) }}
                    />
                  </div>

                  <div className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-gray-800">Fichier 2</p>
                      <span className="text-xs text-gray-500 uppercase">{compareUpload?.metadata.fileType || 'N/A'}</span>
                    </div>
                    <div
                      className="text-sm text-gray-800 whitespace-pre-wrap leading-6"
                      dangerouslySetInnerHTML={{ __html: buildHighlightedHtml(compareResult.rightText, compareResult.rightUnique) }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Compare;
